import { useState } from 'react';
import { useQuery } from 'react-query';
import { Download, Link2 } from 'lucide-react';
import { Button, Select } from '@/components/ds-zta';
import CodeBlock from '@/components/CodeBlock';
import { getDownloadLink, getDownloads, type DownloadLink, type ReleaseComponent } from '../services/downloads';
import { apiErrorMessage } from './modals/iamForm';

export interface ReleaseFile {
  /** File name in the release, e.g. iap-darwin-arm64. */
  file: string;
  label: string;
  /** Name to save it as on the machine, e.g. iap. */
  saveAs: string;
  /** Windows files get a curl.exe command; the rest a POSIX one. */
  windows?: boolean;
}

interface Props {
  component: ReleaseComponent;
  files: ReleaseFile[];
}

function curlCommand(f: ReleaseFile, url: string): string {
  if (f.windows) return `curl.exe -fL -o ${f.saveAs} "${url}"`;
  const chmod = f.saveAs.endsWith('.txt') ? '' : ` && chmod +x ${f.saveAs}`;
  // Single quotes: the signed URL is full of & and = that a shell would eat.
  return `curl -fL -o ${f.saveAs} '${url}'${chmod}`;
}

// ReleaseDownload gets one release file through admin-api. The release bucket
// is private, so there is no permanent URL to print: the member picks a file
// and gets a link signed for a few minutes, as a browser download or as a curl
// command to paste on the machine that needs it.
export default function ReleaseDownload({ component, files }: Props) {
  const [chosen, setChosen] = useState(files[0]?.file ?? '');
  const [link, setLink] = useState<DownloadLink | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { data: info, isLoading: infoLoading } = useQuery(['downloads'], getDownloads, { staleTime: 5 * 60_000 });
  const offered = files.filter((f) => !info?.components?.[component] || info.components[component]!.includes(f.file));
  const selected = offered.find((f) => f.file === chosen) ?? offered[0];

  if (infoLoading) return null;
  if (!info?.configured) {
    return (
      <p className="my-2 text-sm text-[var(--d2d-text-muted)]">
        Downloads are not set up on this server. Ask your administrator for the binary, or build it (Option A).
      </p>
    );
  }

  const create = async () => {
    if (!selected) return;
    setLoading(true);
    setError('');
    setLink(null);
    try {
      setLink(await getDownloadLink(component, selected.file));
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const expires = link ? new Date(link.expires_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div className="my-3 space-y-2">
      <div className="flex flex-wrap items-end gap-2">
        <Select
          className="min-w-[16rem]"
          label="File"
          value={selected?.file ?? ''}
          onChange={(e) => {
            setChosen(e.target.value);
            setLink(null);
            setError('');
          }}
        >
          {offered.map((f) => (
            <option key={f.file} value={f.file}>
              {f.label}
            </option>
          ))}
        </Select>
        <Button
          variant="secondary"
          loading={loading}
          leadingIcon={loading ? undefined : <Link2 className="h-4 w-4" />}
          onClick={create}
          disabled={!selected || loading}
        >
          Create download link
        </Button>
      </div>
      {error && <p className="text-sm text-[var(--d2d-danger)]">Could not create a link: {error}</p>}
      {link && selected && (
        <div>
          <CodeBlock code={curlCommand(selected, link.url)} lang={selected.windows ? 'powershell' : 'bash'} />
          <p className="text-xs text-[var(--d2d-text-muted)]">
            <a className="inline-flex items-center gap-1 underline" href={link.url} rel="noreferrer">
              <Download className="h-3 w-3" />
              Download in this browser
            </a>{' '}
            · The link works until {expires}; create a new one after that. It is for you: don't share it.
          </p>
        </div>
      )}
    </div>
  );
}
